import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import ReactMarkdown from "react-markdown";
import { supabase } from "../../supabaseClient";
import TextEffect from "../../components/CustomTexts/TextEffect";
import Dropzone from "react-dropzone";
import bgIMG from "../../assets/Shabu-Training-2021/ok.jpg";

const TrainingEditor = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (images.length === 0) return setImagePreviews([]);
    const urls = images.map((img) => URL.createObjectURL(img));
    setImagePreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [images]);

  const handleUploadImages = async () => {
    const urls: string[] = [];

    for (const image of images) {
      const fileExt = image.name.split(".").pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `public/training-images/${fileName}`;

      const { error } = await supabase.storage
        .from("training-images")
        .upload(filePath, image, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        console.error("Upload error:", error.message);
        continue;
      }

      const { data } = supabase.storage
        .from("training-images")
        .getPublicUrl(filePath);

      urls.push(data.publicUrl);
    }

    return urls;
  };

  const handleSubmit = async () => {
    if (!title || !content) return alert("Title and content are required");
    setUploading(true);

    const imageUrls = await handleUploadImages();
    const trainingName = title.toLowerCase().replace(/\s+/g, "-");

    const { error } = await supabase
      .from("trainings")
      .insert([
        {
          title,
          content,
          image_url: JSON.stringify(imageUrls),
          training_name: trainingName,
        },
      ]);

    setUploading(false);

    if (error) return alert("Error submitting: " + error.message);
    alert("Training Published!");
    navigate(`/services/training/${trainingName}`);
  };

  return (
    <div>
      {/* Hero */}
      <div
        className="relative h-[60vh] flex items-center justify-center text-white bg-cover bg-center"
        style={{ backgroundImage: `url(${bgIMG})` }}
      >
        <div className="absolute inset-0 bg-black opacity-60 z-0" />
        <div className="z-10">
          <TextEffect text1="Create New Training" className="hero-title" />
        </div>
      </div>

      {/* Form Section */}
      <div className="px-4 sm:px-[5%] py-12 max-w-5xl mx-auto space-y-6">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Training Title"
          className="w-full p-3 border border-gray-300 rounded-md text-lg shadow-sm"
        />

        <Dropzone onDrop={(files) => setImages(files)}>
          {({ getRootProps, getInputProps }) => (
            <div
              {...getRootProps()}
              className="cursor-pointer border-dashed border-2 p-6 text-center rounded-lg bg-gray-50 hover:bg-gray-100 transition"
            >
              <input {...getInputProps()} />
              {images.length ? (
                <p>{images.length} image(s) selected</p>
              ) : (
                <p>Drag & drop or click to upload images</p>
              )}
            </div>
          )}
        </Dropzone>

        {imagePreviews.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {imagePreviews.map((src, idx) => (
              <img
                key={idx}
                src={src}
                alt={`preview-${idx}`}
                className="object-cover w-full h-[150px] rounded border"
              />
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <textarea
            rows={15}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write training content in markdown..."
            className="w-full p-3 border border-gray-300 rounded-md font-mono shadow-sm"
          />
          <div className="p-4 border border-gray-300 rounded-md bg-white overflow-auto shadow-sm">
            <div className="prose max-w-none">
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={uploading}
          className="bg-green-600 text-white px-6 py-3 rounded-lg shadow hover:bg-green-700 transition"
        >
          {uploading ? "Submitting..." : "Publish Training"}
        </button>
      </div>
    </div>
  );
};

export default TrainingEditor;
